import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import AuthModal from "@/components/feature/AuthModal";
import { useGame, INDUSTRY_SPECS } from "@/contexts/GameContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import StockChart from "./components/StockChart";
import OrderBook from "./components/OrderBook";
import TradePanel from "./components/TradePanel";
import ShareholderList from "./components/ShareholderList";
import CompanyStockCard from "./components/CompanyStockCard";
import { useLang } from "@/contexts/LanguageContext";

type DetailTab = "chart" | "orderbook" | "shareholders";

export default function StockMarketPage() {
  const { companies, stockMarket } = useGame();
  const { user, getHolding } = useAuth();
  const { isDark } = useTheme();
  const { t } = useLang();
  const navigate = useNavigate();

  const publicCompanies = useMemo(
    () => Object.values(companies).filter((c) => c.isPublic),
    [companies]
  );

  const [selectedId, setSelectedId] = useState<string>(publicCompanies[0]?.id ?? "");
  const [detailTab, setDetailTab] = useState<DetailTab>("chart");
  const [searchQuery, setSearchQuery] = useState("");

  const selectedCompany = selectedId ? companies[selectedId] : null;
  const selectedMarket = selectedId ? stockMarket[selectedId] : null;
  const holding = selectedId ? getHolding(selectedId) : undefined;

  const filteredCompanies = useMemo(() =>
    publicCompanies.filter((c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      INDUSTRY_SPECS[c.industry].label.includes(searchQuery)
    ),
    [publicCompanies, searchQuery]
  );

  const pageBg = isDark ? "bg-[#0a0a0f]" : "bg-[#f8f8f8]";
  const textPrimary = isDark ? "text-white" : "text-gray-900";
  const textSecondary = isDark ? "text-gray-400" : "text-gray-500";
  const cardBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";
  const inputBg = isDark ? "bg-white/5 border-white/10 text-white placeholder-gray-500" : "bg-white border-gray-200 text-gray-900 placeholder-gray-400";
  const divider = isDark ? "border-white/5" : "border-gray-100";
  const tabBg = isDark ? "bg-[#0f1015] border-white/8" : "bg-white border-gray-200";

  // ポートフォリオ合計
  const portfolioValue = useMemo(() => {
    if (!user) return 0;
    return Object.entries(user.stockPortfolio).reduce((sum, [cId, h]) => {
      const c = companies[cId];
      return sum + (c ? c.stockPrice * h.shares : 0);
    }, 0);
  }, [user, companies]);

  const portfolioCost = useMemo(() => {
    if (!user) return 0;
    return Object.values(user.stockPortfolio).reduce((sum, h) => sum + h.avgBuyPrice * h.shares, 0);
  }, [user]);

  const portfolioPnL = portfolioValue - portfolioCost;

  const DETAIL_TABS: { id: DetailTab; label: string; icon: string }[] = [
    { id: "chart",        label: t("tabChart"),        icon: "ri-line-chart-line" },
    { id: "orderbook",    label: t("tabOrderBook"),    icon: "ri-list-ordered" },
    { id: "shareholders", label: t("tabShareholders"), icon: "ri-group-line" },
  ];

  return (
    <div className={`min-h-screen ${pageBg} transition-colors duration-300`}>
      <Navbar />
      <div className="pt-20 pb-12 px-4 md:px-6 max-w-[1400px] mx-auto">

        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <button
                onClick={() => navigate("/")}
                className={`text-sm cursor-pointer hover:text-amber-400 transition-colors ${textSecondary}`}
              >
                {t("homeNav")}
              </button>
              <i className="ri-arrow-right-s-line text-gray-500 text-sm"></i>
              <span className={`text-sm ${textPrimary}`}>{t("stockMarketTitle")}</span>
            </div>
            <h1 className={`text-2xl font-black ${textPrimary}`}>{t("stockMarketTitle")}</h1>
            <p className={`text-sm mt-1 ${textSecondary}`}>{t("stockMarketDesc")}</p>
          </div>

          {/* Portfolio Summary */}
          {user && (
            <div className={`rounded-2xl border px-5 py-3 flex items-center gap-6 ${cardBg}`}>
              <div>
                <p className={`text-xs ${textSecondary} mb-0.5`}>{t("portfolioValue")}</p>
                <p className={`text-lg font-black ${textPrimary}`}>{portfolioValue.toLocaleString()} <span className={`text-xs font-normal ${textSecondary}`}>coins</span></p>
              </div>
              <div className={`border-l pl-6 ${divider}`}>
                <p className={`text-xs ${textSecondary} mb-0.5`}>{t("portfolioPnL")}</p>
                <p className={`text-lg font-black ${portfolioPnL >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                  {portfolioPnL >= 0 ? "+" : ""}{portfolioPnL.toLocaleString()}
                </p>
              </div>
              <div className={`border-l pl-6 ${divider}`}>
                <p className={`text-xs ${textSecondary} mb-0.5`}>{t("holdingCount")}</p>
                <p className={`text-lg font-black ${textPrimary}`}>{Object.keys(user.stockPortfolio).length}</p>
              </div>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          {/* Left: Company List */}
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center">
                <i className={`ri-search-line text-sm ${textSecondary}`}></i>
              </div>
              <input
                type="text"
                placeholder={t("searchCompany")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full rounded-xl border pl-9 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400/30 ${inputBg}`}
              />
            </div>

            {/* Market Stats */}
            <div className={`rounded-2xl border p-4 ${cardBg}`}>
              <p className={`text-xs font-bold mb-3 ${textSecondary}`}>{t("marketOverview")}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className={`text-xs ${textSecondary}`}>{t("listedCompanies")}</p>
                  <p className={`text-xl font-black ${textPrimary}`}>{publicCompanies.length}</p>
                </div>
                <div>
                  <p className={`text-xs ${textSecondary}`}>{t("totalMarketCap")}</p>
                  <p className={`text-xl font-black ${textPrimary}`}>
                    {(publicCompanies.reduce((s, c) => s + c.stockPrice * c.totalShares, 0) / 1000000).toFixed(1)}M
                  </p>
                </div>
              </div>
            </div>

            {/* Company Cards */}
            <div className="space-y-3">
              {filteredCompanies.length === 0 ? (
                <div className={`rounded-2xl border p-6 text-center ${cardBg}`}>
                  <p className={`text-sm ${textSecondary}`}>{t("noCompanyFound")}</p>
                </div>
              ) : (
                filteredCompanies.map((company) => {
                  const market = stockMarket[company.id];
                  if (!market) return null;
                  return (
                    <CompanyStockCard
                      key={company.id}
                      company={company}
                      market={market}
                      isSelected={selectedId === company.id}
                      onClick={() => setSelectedId(company.id)}
                    />
                  );
                })
              )}
            </div>

            {/* My Portfolio */}
            {user && Object.keys(user.stockPortfolio).length > 0 && (
              <div className={`rounded-2xl border ${cardBg}`}>
                <div className={`px-4 py-3 border-b ${divider}`}>
                  <p className={`text-sm font-bold ${textPrimary}`}>{t("myPortfolio")}</p>
                </div>
                <div className="p-3 space-y-2">
                  {Object.entries(user.stockPortfolio).map(([cId, h]) => {
                    const c = companies[cId];
                    if (!c) return null;
                    const currentVal = c.stockPrice * h.shares;
                    const costVal = h.avgBuyPrice * h.shares;
                    const pnl = currentVal - costVal;
                    const pnlPct = costVal > 0 ? (pnl / costVal) * 100 : 0;
                    return (
                      <button
                        key={cId}
                        onClick={() => setSelectedId(cId)}
                        className={`w-full text-left rounded-xl p-3 transition-all cursor-pointer ${
                          selectedId === cId
                            ? isDark ? "bg-amber-400/10" : "bg-amber-50"
                            : isDark ? "hover:bg-white/3" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className={`text-xs font-bold ${textPrimary}`}>{c.name}</p>
                            <p className={`text-xs ${textSecondary}`}>{h.shares.toLocaleString()}{t("unitShares")}</p>
                          </div>
                          <div className="text-right">
                            <p className={`text-xs font-bold ${textPrimary}`}>{currentVal.toLocaleString()}</p>
                            <p className={`text-xs font-bold ${pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              {pnl >= 0 ? "+" : ""}{pnlPct.toFixed(2)}%
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right: Detail */}
          {selectedCompany && selectedMarket ? (
            <div className="space-y-4">
              {/* Company Info Header */}
              <div className={`rounded-2xl border p-5 ${cardBg}`}>
                <div className="flex flex-wrap items-start gap-4">
                  <div className={`w-14 h-14 flex items-center justify-center rounded-2xl flex-shrink-0 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                    <i className={`${INDUSTRY_SPECS[selectedCompany.industry].icon} text-3xl ${INDUSTRY_SPECS[selectedCompany.industry].color}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h2 className={`text-xl font-black ${textPrimary}`}>{selectedCompany.name}</h2>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-400/15 text-emerald-400 font-bold whitespace-nowrap">{t("listedBadge")}</span>
                    </div>
                    <p className={`text-sm ${textSecondary}`}>{selectedCompany.description}</p>
                  </div>
                  <button
                    onClick={() => navigate(`/company/${selectedCompany.id}`)}
                    className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap ${
                      isDark ? "bg-white/5 text-gray-300 hover:bg-white/10 hover:text-white" : "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:text-gray-900"
                    }`}
                  >
                    <i className="ri-external-link-line"></i>
                    {t("companyDetail")}
                  </button>
                </div>

                {/* Key Stats */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4">
                  {[
                    { label: t("currentPrice"), value: `${selectedCompany.stockPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, unit: "coins" },
                    { label: t("dayHigh"),       value: selectedMarket.dayHigh.toLocaleString(), unit: "coins" },
                    { label: t("dayLow"),        value: selectedMarket.dayLow.toLocaleString(),  unit: "coins" },
                    { label: t("volume"),        value: selectedMarket.dayVolume.toLocaleString(), unit: t("unitShares") },
                  ].map((stat) => (
                    <div key={stat.label} className={`rounded-xl p-3 ${isDark ? "bg-white/3" : "bg-gray-50"}`}>
                      <p className={`text-xs ${textSecondary} mb-0.5`}>{stat.label}</p>
                      <p className={`text-base font-black ${textPrimary}`}>{stat.value}</p>
                      <p className={`text-xs ${textSecondary}`}>{stat.unit}</p>
                    </div>
                  ))}
                </div>

                {/* My Holding */}
                {holding && (
                  <div className={`mt-4 rounded-xl p-3 border ${isDark ? "border-amber-400/20 bg-amber-400/5" : "border-amber-200 bg-amber-50"}`}>
                    <div className="flex flex-wrap gap-4">
                      <div>
                        <p className={`text-xs ${textSecondary}`}>{t("myHolding")}</p>
                        <p className="text-amber-400 font-black">{holding.shares.toLocaleString()} {t("unitShares")}</p>
                      </div>
                      <div>
                        <p className={`text-xs ${textSecondary}`}>{t("avgBuyPrice")}</p>
                        <p className={`font-black ${textPrimary}`}>{holding.avgBuyPrice.toLocaleString()} coins</p>
                      </div>
                      <div>
                        <p className={`text-xs ${textSecondary}`}>{t("evalValue")}</p>
                        <p className={`font-black ${textPrimary}`}>{(selectedCompany.stockPrice * holding.shares).toLocaleString()} coins</p>
                      </div>
                      <div>
                        <p className={`text-xs ${textSecondary}`}>{t("pnlLabel")}</p>
                        {(() => {
                          const pnl = (selectedCompany.stockPrice - holding.avgBuyPrice) * holding.shares;
                          return (
                            <p className={`font-black ${pnl >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                              {pnl >= 0 ? "+" : ""}{pnl.toLocaleString()} coins
                            </p>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Detail Tabs */}
              <div className={`rounded-2xl border overflow-hidden ${tabBg}`}>
                <div className="flex overflow-x-auto">
                  {DETAIL_TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setDetailTab(tab.id)}
                      className={`flex items-center gap-2 px-5 py-4 text-sm font-semibold transition-all cursor-pointer whitespace-nowrap flex-shrink-0 border-b-2 ${
                        detailTab === tab.id
                          ? "border-amber-400 text-amber-400"
                          : isDark
                          ? "border-transparent text-gray-400 hover:text-white"
                          : "border-transparent text-gray-500 hover:text-gray-900"
                      }`}
                    >
                      <i className={`${tab.icon} text-base`}></i>
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content + Trade Panel */}
              <div className="grid grid-cols-1 xl:grid-cols-[1fr_300px] gap-4">
                <div>
                  {detailTab === "chart" && (
                    <StockChart
                      priceHistory={selectedMarket.priceHistory}
                      currentPrice={selectedCompany.stockPrice}
                      prevClose={selectedMarket.prevClose}
                    />
                  )}
                  {detailTab === "orderbook" && (
                    <OrderBook
                      orders={selectedMarket.orders}
                      trades={selectedMarket.trades}
                      currentPrice={selectedCompany.stockPrice}
                    />
                  )}
                  {detailTab === "shareholders" && (
                    <ShareholderList company={selectedCompany} market={selectedMarket} />
                  )}
                </div>

                {/* Trade Panel */}
                <TradePanel company={selectedCompany} holding={holding} />
              </div>
            </div>
          ) : (
            <div className={`rounded-2xl border flex flex-col items-center justify-center py-20 ${cardBg}`}>
              <div className={`w-16 h-16 flex items-center justify-center rounded-2xl mb-4 ${isDark ? "bg-white/5" : "bg-gray-50"}`}>
                <i className="ri-stock-line text-3xl text-amber-400"></i>
              </div>
              <p className={`text-lg font-bold mb-2 ${textPrimary}`}>{t("selectCompany")}</p>
              <p className={`text-sm ${textSecondary}`}>{t("selectCompanyDesc")}</p>
            </div>
          )}
        </div>
      </div>
      <AuthModal />
    </div>
  );
}
